import { createSlice, createSelector } from '@reduxjs/toolkit';
import { createContext, ReducerState, useContext, useEffect, useMemo, useReducer } from 'react';
import qs from 'query-string';

import { IdentityContext } from '../App/identity.context';

import { useLazyQuery } from '@apollo/client';
import { CasdoorContext } from '../App/casdoor.context';
import { useStoreContext } from '../../factorys/useStoreContext';

import { Loader } from '../../components/Loader';
import {
  GetHomePageDataDocument,
  GetHomePageDataQuery,
  HomePageCourseFragmentDoc,
} from '../../services/graphql/types/graphql';
import { getFragmentData } from '../../services/graphql/types';
import { useNavigate } from 'react-router-dom';

import { clearIdentitySelectionStorage } from '../../utils/identityStorage';
import * as Sentry from '@sentry/react';

const storageName = 'HomePageState';

export const defaultState = {
  userHomePageData: undefined as any,
};

const { actions, reducer } = createSlice({
  name: storageName,
  initialState: defaultState,
  reducers: {
    setUserHomePageData: (state, action) => {
      state.userHomePageData = action.payload;
    },
  },
});

export type HomePageStorageState = ReducerState<typeof reducer>;

export const HomePageStoreContext = createContext<
  | {
      state: HomePageStorageState;
      dispatch: React.Dispatch<React.ReducerAction<typeof reducer>>;
      memos: {
        homePageData: GetHomePageDataQuery;
      };
    }
  | undefined
>(undefined);

export const HomeStoreContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, defaultState);

  const { currentClassId, currentSchoolId, currentIdentity, userIdentities } =
    useStoreContext(IdentityContext);

  const { role } = useContext(CasdoorContext);

  const [getHomePageData, { loading, error, data: homePageData, called }] =
    useLazyQuery(GetHomePageDataDocument);

  const isOnlyTestCourse = useMemo(() => {
    return (
      userIdentities.user.userCourses.length === 1 &&
      userIdentities.user.userCourses[0].course.courseId === '1e25cc71-a3e0-4a70-a574-5322a8e95fe5'
    );
  }, [userIdentities]);

  // useEffect(() => {
  //   console.info('homePageData', homePageData);
  // }, [homePageData]);

  useEffect(() => {
    if (currentIdentity?.identity === 'student') {
      if (!isOnlyTestCourse) {
        navigate(`/course`);
      }
      return;
    }
    // currentIdentity 已按 portalView 解析：双身份用户选择教师视图时为 teacher，不会触发跳转
    if (currentIdentity?.identity === 'ta') {
      const query = qs.stringify({
        classId: currentIdentity.classId,
      });
      navigate(`/gradebook?${query}`);
    }
  }, [currentIdentity, homePageData, navigate, loading, isOnlyTestCourse]);

  useEffect(() => {
    if (currentSchoolId && currentClassId && role) {
      getHomePageData({
        variables: {
          schoolId: currentSchoolId,
          classId: currentClassId,
        },
      }).catch(console.info);
    }
  }, [currentSchoolId, currentClassId, role, getHomePageData]);

  useEffect(() => {
    if (error) {
      console.error(error);
      Sentry.captureException(error);

      clearIdentitySelectionStorage();
      localStorage.removeItem('CurrentSchoolId');
      location.reload();
    }
  }, [error]);
  // Show loader if query not called yet, or still loading, or no data
  if (!called || loading || !homePageData || !currentIdentity) {
    return <Loader fullscreen source="HomePageStoreProvider" />;
  }

  return (
    <HomePageStoreContext.Provider
      value={{
        state,
        dispatch,
        memos: {
          homePageData,
        },
      }}
    >
      {children}
    </HomePageStoreContext.Provider>
  );
};

export const HomePageActions = actions;
